import {SpoofedCommitWarning} from '@github-ui/code-view-shared/components/SpoofedCommitWarning'
import {useCodeViewBanners} from '@github-ui/code-view-shared/contexts/CodeViewBannersContext'
import {CurrentTreeProvider, useCurrentTree} from '@github-ui/code-view-shared/contexts/CurrentTreeContext'
import {
  type BlobPayload,
  type FilePagePayload,
  isBlobPayload,
  isTreePayload,
  type TreePayload,
} from '@github-ui/code-view-types'
import {Flash} from '@primer/react'

import {CurrentBlobProvider} from '../hooks/CurrentBlob'
import {forceAnnouncementToScreenReaders} from '../utilities/lines'
import {BlameBanners} from './blob/Banners/BlameBanners'
import BlobLowerBanners from './blob/Banners/BlobLowerBanners'
import BlobMidBanners from './blob/Banners/BlobMidBanners'
import BlobTopBanners from './blob/Banners/BlobTopBanners'

export default function CodeViewBanners({payload}: {payload: FilePagePayload}) {
  if (isTreePayload(payload)) {
    return <TreeBanners tree={payload.tree} />
  } else if (isBlobPayload(payload)) {
    return <BlobBanners blob={payload.blob} />
  }

  return null
}

function TreeBanners({tree}: {tree: TreePayload}) {
  return (
    <CurrentTreeProvider payload={tree}>
      <SpoofedCommitWarning />
      <TruncatedTreeBanner />
      <CodeViewContextBanners />
    </CurrentTreeProvider>
  )
}

function BlobBanners({blob}: {blob: BlobPayload}) {
  return (
    <CurrentBlobProvider blob={blob}>
      <BlobTopBanners />
      <BlobMidBanners />
      <BlobLowerBanners />
      <BlameBanners />
      <CodeViewContextBanners />
    </CurrentBlobProvider>
  )
}

function TruncatedTreeBanner() {
  const {items, totalCount} = useCurrentTree()
  const omittedCount = totalCount - items.length
  return omittedCount > 0 ? (
    <Flash variant="warning" data-testid="repo-truncation-warning" sx={{mt: 3}}>
      Sorry, we had to truncate this directory to {items.length.toLocaleString()} files. {omittedCount.toLocaleString()}{' '}
      {omittedCount === 1 ? 'entry was' : 'entries were'} omitted from the list. Latest commit info may be omitted.
    </Flash>
  ) : null
}

function CodeViewContextBanners() {
  const banners = useCodeViewBanners()
  let messageToAnnounce = ''
  for (const b of banners) {
    messageToAnnounce += b.message
  }
  forceAnnouncementToScreenReaders(messageToAnnounce)
  return (
    <>
      {banners.map((b, i) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Flash key={i} variant={b.variant} sx={{mt: 3}}>
          {b.message}
        </Flash>
      ))}
    </>
  )
}

try{ CodeViewBanners.displayName ||= 'CodeViewBanners' } catch {}
try{ TreeBanners.displayName ||= 'TreeBanners' } catch {}
try{ BlobBanners.displayName ||= 'BlobBanners' } catch {}
try{ TruncatedTreeBanner.displayName ||= 'TruncatedTreeBanner' } catch {}
try{ CodeViewContextBanners.displayName ||= 'CodeViewContextBanners' } catch {}